import { useState, useEffect, useCallback, useRef } from "react";
import { 
  Users, Building2, Plus, Search, Download, FileText, 
  Upload, CheckCircle, XCircle, Loader2, ChevronDown, ChevronRight,
  Phone, Mail, MapPin, Calendar, Eye, Edit2, Save, Trash2, AlertTriangle,
  PlayCircle, ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

export default function ClientesTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedClient, setExpandedClient] = useState(null);
  const [userRole, setUserRole] = useState("admin");
  
  // Modal states
  const [createOpen, setCreateOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [contactForm, setContactForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    password: ""
  });
  
  // Edit client modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    rfc: "",
    address: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  
  // Delete client modal
  const [deleteClientOpen, setDeleteClientOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deletingClient, setDeletingClient] = useState(false);
  
  // Create tramite modal
  const [createTramiteOpen, setCreateTramiteOpen] = useState(false);
  const [createTramiteForClient, setCreateTramiteForClient] = useState(null);
  const [newTramite, setNewTramite] = useState({ title: "", service_type: "", description: "" });
  const [creatingTramite, setCreatingTramite] = useState(false);
  
  // Iniciar Proceso (crear prospecto desde cliente)
  const [iniciarProcesoOpen, setIniciarProcesoOpen] = useState(false);
  const [clienteParaProceso, setClienteParaProceso] = useState(null);
  const [procesoForm, setProcesoForm] = useState({ service_type: "", description: "" });
  const [iniciandoProceso, setIniciandoProceso] = useState(false);
  
  const SERVICE_TYPES = [
    "Permisos de Autoconsumo", 
    "Controles Volumétricos",
    "Gestión Integral", 
    "Suministro de Combustible"
  ];
  
  const fileInputRef = useRef(null);
  const constanciaInputRef = useRef(null);
  const [uploadingConstancia, setUploadingConstancia] = useState(false);
  const [clientForConstancia, setClientForConstancia] = useState(null);

  // Get user role
  useEffect(() => {
    axios.get(`${API}/admin/me`, axCfg)
      .then(res => setUserRole(res.data.role || "admin"))
      .catch(() => setUserRole("admin"));
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/clients-with-documents?search=${search}`, axCfg);
      // Sort alphabetically by company_name
      const sorted = res.data.sort((a, b) => 
        (a.company_name || "").localeCompare(b.company_name || "", 'es')
      );
      setClients(sorted);
    } catch (e) { 
      toast.error("Error al cargar clientes"); 
    }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { 
    const timer = setTimeout(() => fetchClients(), 300);
    return () => clearTimeout(timer);
  }, [fetchClients]);

  // Auto-refresh cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchClients();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchClients]);

  // Open edit modal
  const openEditModal = (client) => {
    setEditingClient(client);
    setEditForm({
      company_name: client.company_name || "",
      contact_name: client.contact_name || "",
      email: client.email || "",
      phone: client.phone || "",
      rfc: client.rfc || "",
      address: client.address || "",
      notes: client.notes || "",
    });
    setEditModalOpen(true);
  };

  // Save client changes
  const saveClientChanges = async () => {
    if (!editForm.company_name.trim()) {
      toast.error("El nombre de la empresa es requerido");
      return;
    }
    if (!editForm.email.trim()) {
      toast.error("El email es requerido");
      return;
    }

    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/clients/${editingClient.id}`,
        editForm,
        axCfg
      );
      toast.success("Cliente actualizado correctamente");
      setEditModalOpen(false);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar cliente");
    } finally {
      setSaving(false);
    }
  };

  // Delete client functions
  const openDeleteClientModal = (client) => {
    setClientToDelete(client);
    setDeleteClientOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    setDeletingClient(true);
    try {
      await axios.delete(`${API}/admin/clients/${clientToDelete.id}`, axCfg);
      toast.success("Cliente eliminado correctamente");
      setDeleteClientOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar cliente");
    } finally {
      setDeletingClient(false);
    }
  };

  // Create tramite for client
  const createTramite = async () => {
    if (!newTramite.title.trim() || !newTramite.service_type) {
      toast.error("Completa el título y tipo de servicio");
      return;
    }
    setCreatingTramite(true);
    try {
      await axios.post(`${API}/admin/tramites`, {
        client_id: createTramiteForClient.id,
        title: newTramite.title,
        service_type: newTramite.service_type,
        description: newTramite.description
      }, axCfg);
      toast.success("Trámite creado exitosamente");
      setCreateTramiteOpen(false);
      setCreateTramiteForClient(null);
      setNewTramite({ title: "", service_type: "", description: "" });
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear trámite");
    } finally {
      setCreatingTramite(false);
    }
  };

  // Abrir modal para iniciar proceso (crear prospecto desde cliente)
  const openIniciarProceso = (client) => {
    setClienteParaProceso(client);
    setProcesoForm({ service_type: "", description: "" });
    setIniciarProcesoOpen(true);
  };

  // Iniciar proceso - crear prospecto vinculado al cliente
  const iniciarProceso = async () => {
    if (!clienteParaProceso || !procesoForm.service_type) {
      toast.error("Seleccione el tipo de servicio");
      return;
    }
    
    setIniciandoProceso(true);
    try {
      const res = await axios.post(`${API}/admin/clients/${clienteParaProceso.id}/iniciar-proceso`, {
        service_type: procesoForm.service_type,
        description: procesoForm.description
      }, axCfg);
      
      toast.success("Proceso iniciado. El cliente aparece en Prospectos.");
      setIniciarProcesoOpen(false);
      setClienteParaProceso(null);
      
      // Mostrar opción de habilitar diagnóstico
      if (res.data.prospecto_id) {
        toast.info("Vaya a la pestaña Prospectos para habilitar el diagnóstico.");
      }
      
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar proceso");
    } finally {
      setIniciandoProceso(false);
    }
  };

  // Handle upload constancia for existing client
  const handleConstanciaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !clientForConstancia) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Solo se permiten archivos PDF");
      return;
    }
    
    setUploadingConstancia(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/admin/clients/${clientForConstancia.id}/upload-constancia`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Constancia subida correctamente. Cliente verificado.");
      setClientForConstancia(null);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir constancia");
    } finally {
      setUploadingConstancia(false);
      if (constanciaInputRef.current) constanciaInputRef.current.value = "";
    }
  };

  // Handle PDF upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Solo se permiten archivos PDF");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(`${API}/admin/parse-constancia-fiscal`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (res.data.success) {
        setExtractedData(res.data.data);
        toast.success("Datos extraidos del PDF");
      } else {
        toast.error(res.data.message || "No se pudieron extraer los datos");
      }
    } catch (e) {
      toast.error("Error al procesar el PDF");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Create client from extracted data
  const createClient = async () => {
    if (!extractedData?.rfc) {
      toast.error("Primero sube una Constancia de Situacion Fiscal");
      return;
    }
    if (!contactForm.contact_name || !contactForm.contact_email || !contactForm.password) {
      toast.error("Completa los datos de contacto y contrasena");
      return;
    }
    
    const formData = new FormData();
    formData.append("rfc", extractedData.rfc);
    formData.append("razon_social", extractedData.razon_social || "");
    formData.append("nombre_comercial", extractedData.nombre_comercial || "");
    formData.append("codigo_postal", extractedData.codigo_postal || "");
    formData.append("nombre_vialidad", extractedData.nombre_vialidad || "");
    formData.append("numero_exterior", extractedData.numero_exterior || "");
    formData.append("nombre_colonia", extractedData.nombre_colonia || "");
    formData.append("nombre_municipio", extractedData.nombre_municipio || "");
    formData.append("nombre_entidad", extractedData.nombre_entidad || "");
    formData.append("stored_filename", extractedData.stored_filename || "");
    formData.append("contact_name", contactForm.contact_name);
    formData.append("contact_email", contactForm.contact_email);
    formData.append("contact_phone", contactForm.contact_phone);
    formData.append("password", contactForm.password);
    
    try {
      await axios.post(`${API}/admin/clients/from-constancia`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Cliente creado exitosamente");
      setCreateOpen(false);
      setExtractedData(null);
      setContactForm({ contact_name: "", contact_email: "", contact_phone: "", password: "" });
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cliente");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/clients`, axCfg);
      const ws = XLSX.utils.json_to_sheet(res.data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Clientes");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `clientes_petrocontrol_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  // Download document
  const downloadDocument = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${docId}/download`, {
        ...axCfg,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      toast.success("Descarga iniciada");
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando clientes...</p>;

  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por RFC, razon social, contacto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-clients"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={exportToExcel}
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Dialog open={createOpen} onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) {
              setExtractedData(null);
              setContactForm({ contact_name: "", contact_email: "", contact_phone: "", password: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1a5c4a] hover:bg-[#143f34]" data-testid="new-client-btn">
                <Plus className="h-4 w-4 mr-2" />Nuevo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Registrar Nuevo Cliente</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Step 1: Upload PDF */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Paso 1: Cargar Constancia de Situacion Fiscal (PDF)</Label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      extractedData ? "border-green-300 bg-green-50" : "border-gray-300 hover:border-[#1a5c4a]"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="upload-constancia-input"
                    />
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-[#1a5c4a]" />
                        <span>Procesando PDF...</span>
                      </div>
                    ) : extractedData ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <CheckCircle className="h-6 w-6" />
                        <span>Datos extraidos correctamente</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">Haz clic para subir la Constancia de Situacion Fiscal del SAT</p>
                        <p className="text-xs text-gray-400 mt-1">Solo archivos PDF</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 2: Review extracted data */}
                {extractedData && (
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Paso 2: Verificar y editar datos extraidos</Label>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
                      {/* Datos Generales - Editables */}
                      <div className="border-b pb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Datos Generales</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-gray-500">RFC</Label>
                            <Input
                              value={extractedData.rfc || ""}
                              onChange={(e) => setExtractedData({...extractedData, rfc: e.target.value.toUpperCase()})}
                              className="h-8 text-sm font-medium"
                              placeholder="RFC"
                              maxLength={13}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">C.P.</Label>
                            <Input
                              value={extractedData.codigo_postal || ""}
                              onChange={(e) => setExtractedData({...extractedData, codigo_postal: e.target.value})}
                              className="h-8 text-sm"
                              placeholder="Código Postal"
                              maxLength={5}
                            />
                          </div>
                        </div>
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">Razón Social *</Label>
                          <Input
                            value={extractedData.razon_social || ""}
                            onChange={(e) => setExtractedData({...extractedData, razon_social: e.target.value})}
                            className="h-8 text-sm font-medium"
                            placeholder="Nombre de la empresa"
                          />
                          <p className="text-xs text-blue-600 mt-1">Puedes editar manualmente si el OCR no extrajo correctamente</p>
                        </div>
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">Nombre Comercial (opcional)</Label>
                          <Input
                            value={extractedData.nombre_comercial || ""}
                            onChange={(e) => setExtractedData({...extractedData, nombre_comercial: e.target.value})}
                            className="h-8 text-sm"
                            placeholder="Nombre comercial"
                          />
                        </div>
                      </div>
                      
                      {/* Domicilio Fiscal - Editable */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Domicilio Fiscal</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-gray-500">Calle</Label>
                            <Input
                              value={extractedData.nombre_vialidad || ""}
                              onChange={(e) => setExtractedData({...extractedData, nombre_vialidad: e.target.value})}
                              className="h-7 text-xs"
                              placeholder="Calle"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">No. Exterior</Label>
                            <Input
                              value={extractedData.numero_exterior || ""}
                              onChange={(e) => setExtractedData({...extractedData, numero_exterior: e.target.value})}
                              className="h-7 text-xs"
                              placeholder="No. Ext"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">No. Interior</Label>
                            <Input
                              value={extractedData.numero_interior || ""}
                              onChange={(e) => setExtractedData({...extractedData, numero_interior: e.target.value})}
                              className="h-7 text-xs"
                              placeholder="No. Int"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Colonia</Label>
                            <Input
                              value={extractedData.nombre_colonia || ""}
                              onChange={(e) => setExtractedData({...extractedData, nombre_colonia: e.target.value})}
                              className="h-7 text-xs"
                              placeholder="Colonia"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Municipio</Label>
                            <Input
                              value={extractedData.nombre_municipio || ""}
                              onChange={(e) => setExtractedData({...extractedData, nombre_municipio: e.target.value})}
                              className="h-7 text-xs"
                              placeholder="Municipio"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Estado</Label>
                            <Input
                              value={extractedData.nombre_entidad || ""}
                              onChange={(e) => setExtractedData({...extractedData, nombre_entidad: e.target.value})}
                              className="h-7 text-xs"
                              placeholder="Estado"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Dirección completa */}
                      <div className="bg-white rounded p-2 border">
                        <span className="text-gray-500 text-xs">Dirección completa:</span>
                        <p className="font-medium text-xs mt-1">
                          {[
                            extractedData.nombre_vialidad,
                            extractedData.numero_exterior ? `#${extractedData.numero_exterior}` : "",
                            extractedData.numero_interior ? `Int. ${extractedData.numero_interior}` : "",
                            extractedData.nombre_colonia ? `Col. ${extractedData.nombre_colonia}` : "",
                            extractedData.nombre_localidad,
                            extractedData.nombre_municipio,
                            extractedData.nombre_entidad,
                            extractedData.codigo_postal ? `C.P. ${extractedData.codigo_postal}` : "",
                            extractedData.pais || "México"
                          ].filter(Boolean).join(", ") || "Sin datos de dirección"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Contact info */}
                {extractedData && (
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Paso 3: Datos de contacto y acceso al portal</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nombre del Contacto *</Label>
                        <Input
                          value={contactForm.contact_name}
                          onChange={(e) => setContactForm({...contactForm, contact_name: e.target.value})}
                          placeholder="Juan Perez"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Telefono</Label>
                        <Input
                          value={contactForm.contact_phone}
                          onChange={(e) => setContactForm({...contactForm, contact_phone: e.target.value})}
                          placeholder="33 1234 5678"
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Email del Contacto *</Label>
                      <Input
                        type="email"
                        value={contactForm.contact_email}
                        onChange={(e) => setContactForm({...contactForm, contact_email: e.target.value})}
                        placeholder="contacto@empresa.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Contrasena para Portal *</Label>
                      <Input
                        type="password"
                        value={contactForm.password}
                        onChange={(e) => setContactForm({...contactForm, password: e.target.value})}
                        placeholder="Minimo 6 caracteres"
                        data-testid="contact-password-input"
                      />
                    </div>
                    <Button 
                      onClick={createClient} 
                      className="w-full bg-[#1a5c4a] hover:bg-[#143f34]"
                      data-testid="create-client-submit"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Crear Cliente
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="text-sm text-gray-500">
        {clients.length} clientes registrados
      </div>

      {/* Clients list - compact view */}
      {clients.length === 0 ? (
        <Card className="border-none">
          <CardContent className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay clientes {search ? "con ese criterio" : "registrados"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {clients.map((client) => (
            <Card 
              key={client.id} 
              className="border-none shadow-sm"
              data-testid={`client-row-${client.id}`}
            >
              <CardContent className="p-0">
                {/* Main row - compact */}
                <div 
                  className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                >
                  <button className="text-gray-400">
                    {expandedClient === client.id ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  <div className="w-8 h-8 rounded-lg bg-[#1a5c4a]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-4 w-4 text-[#1a5c4a]" />
                  </div>
                  
                  <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-center text-sm">
                    <div className="col-span-1 font-mono text-[10px] text-gray-500 truncate">
                      {client.rfc || "-"}
                    </div>
                    <div className="col-span-3 font-medium text-gray-900 truncate">
                      {client.company_name}
                    </div>
                    <div className="col-span-2 text-gray-600 truncate text-xs">
                      {client.contact_name}
                    </div>
                    <div className="col-span-2 text-gray-400 truncate text-[10px]">
                      {client.email}
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-[10px] text-gray-500">{client.total_tramites || 0} trám.</span>
                    </div>
                    <div className="col-span-3 flex items-center justify-end gap-2">
                      {/* Iconos de acción compactos */}
                      <div className="flex items-center gap-1 mr-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 rounded-full hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(client);
                          }}
                          title="Editar cliente"
                        >
                          <Edit2 className="h-3 w-3 text-gray-400" />
                        </Button>
                        {userRole === "admin" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 rounded-full hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteClientModal(client);
                            }}
                            title="Eliminar cliente"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="h-3 w-3 text-gray-400 hover:text-red-500" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Botones de acción principales */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          openIniciarProceso(client);
                        }}
                        title="Iniciar proceso de embudo"
                        data-testid={`iniciar-proceso-${client.id}`}
                      >
                        <PlayCircle className="h-3 w-3 mr-1" />
                        Proceso
                      </Button>
                      <Button
                        size="sm"
                        className="h-6 px-2 text-[10px] font-medium bg-[#1a5c4a] hover:bg-[#143f34] text-white rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreateTramiteForClient(client);
                          setCreateTramiteOpen(true);
                        }}
                        title="Crear trámite"
                        data-testid={`create-tramite-${client.id}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Trámite
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {expandedClient === client.id && (
                  <div className="px-4 pb-4 pt-2 border-t bg-gray-50/50">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Contact info */}
                      <div className="space-y-2 text-sm">
                        <h4 className="font-semibold text-gray-700">Informacion de Contacto</h4>
                        <div className="space-y-1 text-gray-600">
                          <p className="flex items-center gap-2">
                            <Mail className="h-3 w-3" /> {client.email}
                          </p>
                          {client.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="h-3 w-3" /> {client.phone}
                            </p>
                          )}
                          {client.direccion && (
                            <p className="flex items-center gap-2">
                              <MapPin className="h-3 w-3" /> {client.direccion}
                            </p>
                          )}
                          <p className="flex items-center gap-2">
                            <Calendar className="h-3 w-3" /> 
                            Registrado: {new Date(client.created_at).toLocaleDateString('es-MX')}
                            {client.created_by_user_name && (
                              <span className="text-[#1a5c4a] font-medium ml-1">
                                por {client.created_by_user_name}
                              </span>
                            )}
                            {client.created_by_source === "landing_page" && (
                              <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded">
                                Landing Page
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Documents */}
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-700 text-sm">Documentos ({client.documents?.length || 0})</h4>
                        {client.documents?.length > 0 ? (
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {client.documents.map((doc) => (
                              <div 
                                key={doc.id}
                                className="flex items-center justify-between p-2 bg-white rounded border text-xs"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="h-3 w-3 text-[#1a5c4a] flex-shrink-0" />
                                  <span className="truncate">{doc.original_filename}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadDocument(doc.id, doc.original_filename);
                                  }}
                                  data-testid={`download-doc-${doc.id}`}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">Sin documentos</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Client Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#1a5c4a]" />
              Editar Cliente
            </DialogTitle>
            <DialogDescription>
              Modifica la información del cliente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Empresa / Razón Social *</Label>
                <Input
                  className="mt-1"
                  value={editForm.company_name}
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                  placeholder="Nombre de la empresa"
                />
              </div>
              <div>
                <Label>RFC</Label>
                <Input
                  className="mt-1"
                  value={editForm.rfc}
                  onChange={(e) => setEditForm({ ...editForm, rfc: e.target.value.toUpperCase() })}
                  placeholder="RFC"
                  maxLength={13}
                />
              </div>
              <div>
                <Label>Contacto</Label>
                <Input
                  className="mt-1"
                  value={editForm.contact_name}
                  onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                  placeholder="Nombre del contacto"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  className="mt-1"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="correo@empresa.com"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  className="mt-1"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="10 dígitos"
                />
              </div>
              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input
                  className="mt-1"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="Dirección fiscal"
                />
              </div>
              <div className="col-span-2">
                <Label>Notas</Label>
                <Textarea
                  className="mt-1"
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Notas adicionales sobre el cliente..."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={saveClientChanges}
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation Modal */}
      <Dialog open={deleteClientOpen} onOpenChange={setDeleteClientOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Cliente
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer
            </DialogDescription>
          </DialogHeader>

          {clientToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ¿Estás seguro de eliminar el siguiente cliente?
                </p>
                <div className="bg-white rounded p-3 border border-red-100">
                  <p className="font-semibold text-gray-900">{clientToDelete.company_name}</p>
                  <p className="text-sm text-gray-600">RFC: {clientToDelete.rfc || "N/A"}</p>
                  <p className="text-sm text-gray-500">Contacto: {clientToDelete.contact_name}</p>
                  <p className="text-sm text-gray-500">{clientToDelete.email}</p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Advertencia:</strong> Al eliminar este cliente también se eliminarán:
                </p>
                <ul className="list-disc ml-5 mt-1 text-sm text-amber-700">
                  <li>Todos sus trámites ({clientToDelete.total_tramites || 0})</li>
                  <li>Todos los documentos asociados</li>
                  <li>El historial completo</li>
                  <li>Los archivos subidos</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteClientOpen(false);
                setClientToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteClient}
              disabled={deletingClient}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingClient ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sí, Eliminar Cliente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Tramite Modal */}
      <Dialog open={createTramiteOpen} onOpenChange={(open) => {
        setCreateTramiteOpen(open);
        if (!open) {
          setCreateTramiteForClient(null);
          setNewTramite({ title: "", service_type: "", description: "" });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#1a5c4a]" />
              Crear Trámite
            </DialogTitle>
            <DialogDescription>
              {createTramiteForClient && (
                <span>Para: <strong>{createTramiteForClient.company_name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tramite-title">Título del Trámite *</Label>
              <Input
                id="tramite-title"
                placeholder="Ej: Permiso de Autoconsumo 2026"
                value={newTramite.title}
                onChange={(e) => setNewTramite({ ...newTramite, title: e.target.value })}
                data-testid="tramite-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de Servicio *</Label>
              <Select
                value={newTramite.service_type}
                onValueChange={(value) => setNewTramite({ ...newTramite, service_type: value })}
              >
                <SelectTrigger className="w-full" data-testid="tramite-service-select">
                  <SelectValue placeholder="Seleccionar tipo de servicio" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tramite-desc">Descripción (opcional)</Label>
              <Textarea
                id="tramite-desc"
                placeholder="Notas adicionales sobre el trámite..."
                value={newTramite.description}
                onChange={(e) => setNewTramite({ ...newTramite, description: e.target.value })}
                rows={3}
                data-testid="tramite-desc-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setCreateTramiteOpen(false);
                setCreateTramiteForClient(null);
                setNewTramite({ title: "", service_type: "", description: "" });
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={createTramite}
              disabled={creatingTramite || !newTramite.title.trim() || !newTramite.service_type}
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              data-testid="create-tramite-submit"
            >
              {creatingTramite ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Trámite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden input for constancia upload */}
      <input
        type="file"
        ref={constanciaInputRef}
        className="hidden"
        accept=".pdf"
        onChange={handleConstanciaUpload}
      />

      {/* Modal Iniciar Proceso */}
      <Dialog open={iniciarProcesoOpen} onOpenChange={setIniciarProcesoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <PlayCircle className="h-5 w-5" />
              Iniciar Proceso de Embudo
            </DialogTitle>
            <DialogDescription>
              Se creará un prospecto vinculado a este cliente para iniciar el flujo completo: 
              Diagnóstico → Cotización → Validación → Contrato → Pre-Trámite
            </DialogDescription>
          </DialogHeader>
          
          {clienteParaProceso && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="font-medium text-gray-900">{clienteParaProceso.company_name}</p>
                <p className="text-sm text-gray-600">{clienteParaProceso.contact_name}</p>
                <p className="text-sm text-gray-500">{clienteParaProceso.email}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Tipo de Servicio *</Label>
                  <Select 
                    value={procesoForm.service_type} 
                    onValueChange={(v) => setProcesoForm({...procesoForm, service_type: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccione el servicio..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Descripción / Notas (opcional)</Label>
                  <Textarea
                    value={procesoForm.description}
                    onChange={(e) => setProcesoForm({...procesoForm, description: e.target.value})}
                    placeholder="Detalles adicionales sobre el requerimiento..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <p className="text-xs text-amber-800">
                  <strong>Siguiente paso:</strong> Después de crear el prospecto, vaya a la pestaña 
                  "Prospectos" y haga clic en "Habilitar Diag." para enviar el formulario de diagnóstico al cliente.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIniciarProcesoOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={iniciarProceso}
              disabled={iniciandoProceso || !procesoForm.service_type}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {iniciandoProceso ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Iniciando...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Iniciar Proceso
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
